<template>
<!--
    Bildfeld als breite, beschriftete Uploadfläche.

    Vorher: kleine 288-Pixel-Karte als "inline-block" mit einem
    unbeschrifteten Upload-Icon. Jetzt: gemeinsame Feldhülle, grosses
    Vorschaubild, sichtbare Dateianforderungen und ein beschrifteter
    Hauptbutton. Die Upload-, Lösch- und Fehlerlogik ist unverändert.
-->
<editor-field-frame :label="props.label"
                    :hint="props.hint"
                    :warning="warnings.length ? warnings[0] : null"
                    :status="is_uploading ? $t('editor.fields.image.uploading', {progress: upload_progress || 0}) : null">
    <div class="editor-image-field"
         @dragover.prevent="handleDragOver"
         @drop.prevent="handleFileDrop">

        <!-- Drag Handle nur innerhalb einer Bilderliste -->
        <div v-if="repeatableChild" class="d-flex align-center px-4 pt-3" style="gap: 8px;">
            <v-icon class="drag-handle" :title="$t('common.actions.move')">mdi-drag-vertical</v-icon>
            <span class="text-body-2 font-weight-medium">{{ props.label }}</span>
        </div>

        <v-img :src="imageUrl"
               :lazy-src="`${base_url}/resources/redcode/img/favicon/apple-touch-icon.png`"
               class="editor-image-field__preview"
               :alt="props.label ? $t('editor.fields.image.alt', {label: props.label}) : $t('editor.fields.image.alt_unnamed')"
               max-height="320"
               @click="handleImageClick">
            <div v-if="is_uploading" class="d-flex align-center justify-center fill-height">
                <v-progress-circular :rotate="0" :size="56" :width="5"
                                     :model-value="upload_progress" color="primary">
                    {{ upload_progress || 0 }}%
                </v-progress-circular>
            </div>
        </v-img>

        <p class="editor-image-field__requirements">
            {{ requirementsText }}
        </p>

        <div class="editor-image-field__actions">
            <input type="file" ref="uploader" style="display: none" @change="handleFileSelect">

            <!-- Beschrifteter Hauptbutton statt alleinstehendem Icon -->
            <v-btn color="primary" variant="flat" class="normal-case"
                   prepend-icon="mdi-tray-arrow-up"
                   :loading="is_uploading"
                   @click="toggleFileSelect">
                {{ value ? $t('editor.fields.image.replace') : $t('editor.fields.image.choose') }}
            </v-btn>

            <v-spacer />

            <!-- Seltene Zusatzaktionen im Menü -->
            <v-menu>
                <template v-slot:activator="{ props: menuProps }">
                    <v-btn v-bind="menuProps" variant="text" class="normal-case"
                           append-icon="mdi-chevron-down">{{ $t('common.actions.more_actions') }}</v-btn>
                </template>
                <v-list density="compact">
                    <v-list-item :disabled="!value" prepend-icon="mdi-open-in-new"
                                 @click="handleShowCurrent">{{ $t('editor.fields.image.view_current') }}</v-list-item>
                    <v-list-item v-if="hasOriginal" :disabled="!value" prepend-icon="mdi-image-refresh-outline"
                                 @click="handleShowOriginal">{{ $t('editor.fields.image.view_original') }}</v-list-item>
                    <v-list-item :disabled="!isDeletable" prepend-icon="mdi-undo-variant"
                                 @click="handleDelete">
                        <v-list-item-title>{{ $t('editor.fields.image.remove_own') }}</v-list-item-title>
                        <v-list-item-subtitle>{{ $t('editor.fields.image.remove_own_hint') }}</v-list-item-subtitle>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>

        <!-- Weitere Warnungen unterhalb, in derselben Feldhülle -->
        <div v-if="warnings.length > 1" class="px-4 pb-3">
            <v-alert v-for="(warning, index) in warnings.slice(1)" :key="index"
                     type="warning" variant="tonal" density="compact" class="mb-2"
                     closable @click:close="warnings.splice(index + 1, 1)">
                {{ warning }}
            </v-alert>
        </div>
    </div>
</editor-field-frame>
</template>

<script setup>
import {computed, ref, onMounted} from "vue";
import EditorFieldFrame from "./EditorFieldFrame.vue";
import {humanFileSize} from "../../utils/helpers";
import * as api from "../../services/api";
// https://www.npmjs.com/package/jsonpath
import jp from "jsonpath";
import {getStoreByName} from "../../utils/storeHelpers";
import {mandatory_props, resolveFieldPath} from "../../utils/editorConfigFieldHelpers";
import {persistEditorFieldChange} from "../../utils/editorFieldPersistence.mjs";
import {useCampaignStore} from "../../Stores/CampaignStore";
import {useI18n} from "@/i18n";

const {t} = useI18n();

defineExpose({toggleFileSelect});

const max_upload_size = 500000; // 500 KB
// ACHTUNG: muss zu App\Rules\ImageDimensions::MAX_EDGE passen
const max_edge = 3440; // Pixel je Kante
const warnings = ref([]);
const is_uploading = ref(false);
const uploader = ref(null);
const upload_progress = ref(0);

const base_url = import.meta.env.VITE_DELIVERY_SYSTEM_URL;
const props = defineProps({
    ...mandatory_props,
    repeatableChild: {
        type: Boolean,
        default: false,
    }
});
const store = getStoreByName(props.storeName);
const campaignStore = useCampaignStore();
/**
 * The effective json_path of this field.
 * @see resolveFieldPath() - handles fields inside (repeatable) SubPanels
 */
const jsonPath = computed(() => resolveFieldPath(store, props.json_path));
const value = computed({
    get() {
        return jp.value(store[props.jsonKey], jsonPath.value);
    },
    set(v) {
        jp.value(store[props.jsonKey], jsonPath.value, v);
    }
});

const imageUrl = computed(() => {
    if (value.value) {
        if (value.value.startsWith('http')) return value.value;
        return `${base_url}${value.value}`;
    }
    return null;
});

const emits = defineEmits(['deleted']);

/**
 * Dateianforderungen als sichtbarer Text statt nur als Tooltip am Icon.
 */
const requirementsText = computed(() => t('editor.fields.image.requirements', {
    size: humanFileSize(max_upload_size, true),
    edge: max_edge,
}));

// ----------------------------------------------------------------------------
// Show Assets
// ----------------------------------------------------------------------------

function handleImageClick() {
    // Click on placeholder image
    if (value.value === '') return toggleFileSelect();
    // click on "real" image
    handleShowCurrent();
}

/**
 * Show the current (customised) asset
 */
function handleShowCurrent() {
    const w = window.open(`${base_url}${value.value}`, '_blank');
    w.focus();
}


const hasOriginal = computed(() => {
    return props?.component_config?.has_original ?? true;
});

/**
 * Show the original asset
 */
function handleShowOriginal() {
    console.log(store);
    const url = window.route('assets.getOriginal', {
        step: store.uuid,
        json_path: jsonPath.value
    });

    api.client.get(url).then(response => {
        const w = window.open(`${base_url}${response.data.original_path}`, '_blank');
        w.focus();
    });
}

// ----------------------------------------------------------------------------
// File Upload
// ----------------------------------------------------------------------------
function toggleFileSelect() {
    uploader.value.click();
}
// Do not delete this function, it is needed for the drag and drop function!
function handleDragOver(event) { }
function handleFileDrop(event) {
    // Prevent default behavior (Prevent file from being opened)
    event.preventDefault();
    let source = event.dataTransfer.items || event.dataTransfer.files;

    // Use DataTransferItemList interface to access the file(s)
    for (let i = 0; i < source.length; i++) {
        // If dropped items aren't files, reject them
        if (source[i].kind === 'file') {
            let file = source[i].getAsFile();
            acceptFile(file);
        }
    }
}
function handleFileSelect(event) {
    for (let i = 0; i < event.target.files.length; i++) {
        let file = event.target.files[i];
        acceptFile(file);
    }
}
function uploadFileForStep(file) {
    const url = store.getAssetUploadUrl();
    const form = new FormData();
    form.append('asset', file);

    const previousValue = value.value;

    let config = {
        onUploadProgress: (progressEvent) => {
            upload_progress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
    };

    is_uploading.value = true;

    api.client.post(url, form, config).then(response => {
        // Overwrite Image Path with new path
        value.value = response.data.path;

        const field = getCampaignFieldFromJsonPath();

        switch (field) {
            // In case we use this ImageField inside the Landingpage Editor
            case 'meta':
                return campaignStore
                    .updateMeta(getComponentEditorBaseRevision('meta'))
                    .then(updateResponse => {
                        syncComponentEditorRevision(updateResponse, 'meta');
                    });

            case 'components':
                return campaignStore
                    .updateComponents(getComponentEditorBaseRevision('components'))
                    .then(updateResponse => {
                        syncComponentEditorRevision(updateResponse, 'components');
                    });

            // For normal apps/steps
            default:
                // Step-Editoren speichern ausschließlich über ihre
                // Abschlussaktion. Der Upload legt nur die Datei ab und trägt
                // den Pfad in den lokalen Draft ein.
                return persistEditorFieldChange(props.storeName, () => store.save());
        }
    }).catch(error => {
        handleImageSaveError(error, previousValue);
    }).finally(() => {
        is_uploading.value = false;
    });
}

function addWarning(file) {
    warnings.value.push(t('editor.fields.upload.too_large', {
        name: file.name,
        size: humanFileSize(file.size, true),
        max: humanFileSize(max_upload_size, true),
    }));
}

function addDimensionWarning(file, width, height) {
    // Zielgroesse wie in ImageDimensions: laengere Kante exakt auf das Limit,
    // kuerzere nach Seitenverhaeltnis. Kein gemeinsamer Faktor, der rundet daneben.
    const target = width >= height
        ? `${max_edge} × ${Math.max(1, Math.round(height * max_edge / width))}`
        : `${Math.max(1, Math.round(width * max_edge / height))} × ${max_edge}`;
    warnings.value.push(t('editor.fields.image.too_many_pixels', {
        name: file.name,
        width,
        height,
        max: max_edge,
        target,
    }));
}

/*
 * Abmessungen aus der Datei lesen, ohne sie hochzuladen. Nur fuer Rasterbilder —
 * SVG hat keine Pixelabmessungen. Laesst sich das Bild nicht laden, geben wir es
 * weiter: die Entscheidung faellt dann serverseitig, wo sie ohnehin verbindlich
 * ist.
 */
function readDimensions(file) {
    if (!/^image\/(jpeg|png|gif|webp)$/.test(file.type)) return Promise.resolve(null);

    return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({width: img.naturalWidth, height: img.naturalHeight});
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        img.src = url;
    });
}

/*
 * Gemeinsame Vorpruefung fuer Auswahl und Drag-and-drop. Spart dem Benutzer die
 * Upload-Runde, ist aber keine Sicherung: verbindlich pruefen die Regeln
 * ImageDimensions und ConvertibleImage auf dem Server.
 */
async function acceptFile(file) {
    if (file.size >= max_upload_size) {
        addWarning(file);
        return;
    }

    const size = await readDimensions(file);
    if (size && (size.width > max_edge || size.height > max_edge)) {
        addDimensionWarning(file, size.width, size.height);
        return;
    }

    uploadFileForStep(file);
}

function getCampaignFieldFromJsonPath() {
    const parsed = jp.parse(jsonPath.value);

    return parsed[2]?.expression?.value || null;
}

function getComponentEditorBaseRevision(field) {
    if (props.storeName !== 'ComponentEditStore') {
        return null;
    }

    if (store.editingRevisionField !== field) {
        return null;
    }

    return store.baseRevision;
}

function syncComponentEditorRevision(responseData, field) {
    if (props.storeName !== 'ComponentEditStore') {
        return;
    }

    if (typeof store.syncBaseRevisionFromCampaignResponse === 'function') {
        store.syncBaseRevisionFromCampaignResponse(responseData, field);
    }
}

function handleImageSaveError(error, previousValue = null) {
    if (previousValue !== null) {
        value.value = previousValue;
    }

    if (campaignStore.handleExpiredEditorSession(error)) {
        return;
    }

    if (campaignStore.handleStaleRevision(error)) {
        return;
    }

    console.error(error);
}

// ----------------------------------------------------------------------------
// Delete / Restore
// ----------------------------------------------------------------------------

/**
 * Customised Assets are stored in the "/accounts/" folder
 *
 * @type {ComputedRef<*>}
 */
const isDeletable = computed(() => {
    return value.value === "" || value.value?.startsWith('/accounts');
});

/**
 * Rückfrage vor dem Löschen: mit Bildname, falls es einen gibt, und mit
 * dem Hinweis aufs Original, falls es eines gibt.
 */
function confirmDeleteMessage() {
    if (props.label) {
        return hasOriginal.value
            ? t('editor.fields.image.confirm_delete_restore_named', {label: props.label})
            : t('editor.fields.image.confirm_delete_named', {label: props.label});
    }

    return hasOriginal.value
        ? t('editor.fields.image.confirm_delete_restore')
        : t('editor.fields.image.confirm_delete');
}

function handleDelete() {
    if (props.storeName === "ComponentEditStore") {
        handleDeleteCustom();
    } else {
        handleDeleteCustomised();
    }
}

/**
 * handle delete customised (asset).
 * - Tell the server to delete the customised asset
 * - "We" (the client) then overwrite the current image with the original_path
 *   and then tell the server to save the current config from the store
 */
function handleDeleteCustomised() {
    // If the image is "empty" just remove it from the list
    if (value.value === "") {
        emits('deleted', jsonPath.value);
        return;
    }

    if (!window.confirm(confirmDeleteMessage())) {
        return;
    }

    const url = store.getAssetDeleteUrl(jsonPath.value);

    api.client.delete(url).then(response => {
        if (response.data.original_path) {
            // Overwrite Image Path with original path
            value.value = response.data.original_path;
            // Im StepEditor bleibt auch das Wiederherstellen ein lokaler
            // Draft. Andere Editoren behalten ihr bisheriges Persistenzmodell.
            persistEditorFieldChange(props.storeName, () => store.save());
        } else {
            // There is no original, which means we are probably inside a repeater field.
            // Inform ImageRepeaterField via event (it will remove this ImageField from the list)
            emits('deleted', jsonPath.value);
        }
    });
}

/**
 * Handle Delete Custom
 * - There is not default!
 * - This is most likely used on the landingpage components
 */
function handleDeleteCustom() {
    // If the image is "empty" just remove it from the list
    if (value.value === "") {
        emits('deleted', jsonPath.value);
        return;
    }

    if (!window.confirm(confirmDeleteMessage())) {
        return;
    }

    // Use the "real" asset storage path
    const url = store.getAssetDeleteUrl({path: value.value});

    const previousValue = value.value;
    const field = getCampaignFieldFromJsonPath();

    // Set the current value to empty
    value.value = "";

    if (field === "components") {
        // Update the whole components json, and THEN delete the asset
        campaignStore
            .updateComponents(getComponentEditorBaseRevision('components'))
            .then(updateResponse => {
                syncComponentEditorRevision(updateResponse, 'components');
                return api.client.delete(url);
            })
            .catch(error => {
                handleImageSaveError(error, previousValue);
            });

        return;
    }

    if (field === "meta") {
        // Update the whole meta json, and THEN delete the asset
        campaignStore
            .updateMeta(getComponentEditorBaseRevision('meta'))
            .then(updateResponse => {
                syncComponentEditorRevision(updateResponse, 'meta');
                return api.client.delete(url);
            })
            .catch(error => {
                handleImageSaveError(error, previousValue);
            });

        return;
    }

    value.value = previousValue;
}
</script>

<style scoped>

</style>
